import { BotonComentario } from "@/components/Comentarios"
import { useQuery } from "@tanstack/react-query"

import { DataState } from "@/components/DataState"
import { WorkPanel } from "@/components/WorkPanel"
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table"
import { useAuth } from "@/lib/auth"
import { formatAmount, formatDate } from "@/lib/format"
import { cn } from "@/lib/utils"

import { useBooksFilters } from "@/routes/books/context"
import { fetchBalanceSheet, type StatementLine } from "@/routes/statements/api"
import { EmptyBook, StatementCheck, UnmappedWarning } from "@/routes/statements/StatementChecks"
import { BotonDescargar } from "@/components/BotonDescargar"
import { bookToApi } from "@/routes/books/mapping"
import { t } from "@/lib/i18n"

/**
 * Balance general: activo contra pasivo mas patrimonio, a una fecha.
 *
 * Se presenta en DOS COLUMNAS y no en una lista larga porque la forma es el
 * mensaje: la igualdad entre los dos lados es lo que hace que un balance sea un
 * balance, y una lista vertical la esconde detras de un scroll.
 */
export function BalanceSheetPage() {
  const { book, to } = useBooksFilters()
  const { activeTenantId, activeEntityId } = useAuth()

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["balance-sheet", activeTenantId, book, to],
    queryFn: () => fetchBalanceSheet({ book, cutoff: to }),
  })

  return (
    <div className="space-y-3">
      {/*
        LA DESCARGA VA ARRIBA y no dentro de una de las tres hojas: el balance
        se pinta en tres paneles —activo, pasivo, patrimonio— y el archivo trae
        los tres. Un botón dentro de «Activo» se leería como «bajar el activo».
      */}
      <div className="flex justify-end">
        <BotonDescargar
          recurso="balance-general"
          filtros={{ entity_id: activeEntityId, libro: bookToApi(book), hasta: to }}
        />
      </div>

      {data ? (
        <>
          <StatementCheck
            cuadra={data.comprobacion.cuadra}
            diferencia={data.comprobacion.diferencia}
            etiqueta={
              data.comprobacion.cuadra
                ? "Activo = Pasivo + Patrimonio"
                : "El balance NO cuadra"
            }
            detalle={`${formatAmount(data.comprobacion.activo)} = ${formatAmount(
              data.comprobacion.pasivo_mas_patrimonio,
            )} al ${formatDate(data.corte)}`}
          />
          <EmptyBook asientos={data.asientos} />
          <UnmappedWarning noMapeado={data.no_mapeado} />
        </>
      ) : null}

      <DataState
        loading={isLoading}
        error={error}
        onRetry={() => void refetch()}
        empty={false}
        columns={[50, 50]}
      >
        {data ? (
          <div className="grid gap-3 lg:grid-cols-2">
            <Side title={t("Activo")} total={data.activo.total} lineas={data.activo.lineas} />
            <div className="space-y-3">
              <Side title={t("Pasivo")} total={data.pasivo.total} lineas={data.pasivo.lineas} />
              <Side
                title={t("Patrimonio")}
                total={data.patrimonio.total}
                lineas={data.patrimonio.lineas}
                footnote={data.comprobacion.nota}
              />
            </div>
          </div>
        ) : null}
      </DataState>
    </div>
  )
}

function Side({
  title,
  total,
  lineas,
  footnote,
}: {
  title: string
  total: number
  lineas: StatementLine[]
  footnote?: string
}) {
  return (
    <WorkPanel
      title={title}
      meta={<span className="num-strong text-sm">{formatAmount(total)}</span>}
      footnote={footnote}
    >
      {lineas.length === 0 ? (
        <p className="px-2.5 py-3 text-2xs text-muted-foreground">{t("Sin saldos.")}</p>
      ) : (
        <Table>
          <TableBody>
            {lineas.map((linea) => (
              <TableRow key={`${linea.codigo}-${linea.nombre}`}>
                <TableCell className="code w-20">
                  <span className="flex items-center gap-1">
                    {linea.codigo}
                    <BotonComentario ancla={{ ambito: "general", referencia: `cuenta:${linea.codigo}`,
                                              rotulo: `${linea.codigo} ${linea.nombre}` }} />
                  </span>
                </TableCell>
                <TableCell className="truncate">
                  {linea.nombre}
                  {linea.derivado ? (
                    /*
                      El resultado del ejercicio NO es un saldo del libro:
                      mientras el ano no se cierra, su importe no esta asentado
                      en el patrimonio. Quien lo busque en el mayor no lo va a
                      encontrar, asi que la pantalla tiene que decirlo aqui.
                    */
                    <span
                      className="ml-1.5 rounded-sm border border-border-strong px-1 py-px text-2xs uppercase tracking-[0.06em] text-muted-foreground"
                      title={t("No sale de un saldo del libro: se calcula. Mientras el ejercicio no se cierre, su importe no esta asentado en el patrimonio.")}
                    >
                      derivado
                    </span>
                  ) : null}
                </TableCell>
                <TableCell className={cn("num w-32", !linea.importe && "num-zero")}>
                  {linea.importe ? formatAmount(linea.importe) : "—"}
                </TableCell>
              </TableRow>
            ))}
            <TableRow className="hover:bg-transparent">
              <TableCell colSpan={2} className="rule-double text-2xs font-semibold uppercase tracking-[0.06em]">
                {t("Total")}{" "}{title.toLowerCase()}
              </TableCell>
              <TableCell className="num-strong rule-double">{formatAmount(total)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      )}
    </WorkPanel>
  )
}
